import { Component, Vue, Prop, Watch, Ref } from 'vue-property-decorator'

interface TreeNode {
    id: string | number
    name: string
    children?: TreeNode[]
    [key: string]: any
}

@Component
export default class DepartmentTree extends Vue {
    @Prop({ type: Array, default: () => [] }) readonly treeData!: TreeNode[]
    @Prop({ type: Array, default: () => [] }) readonly defaultCheckedKeys!: (string | number)[]

    @Ref() readonly treeRef!: HTMLFormElement

    private treeKey: number = Date.now()
    private checkTimeout: number | null = null

    private isProcessing: boolean = false // 防止重复处理
    private defaultProps = {
        label: 'name',
        children: 'children'
    }

    // 获取选中节点
    getCheckedNodes(): TreeNode[] {
        return this.treeRef?.getCheckedNodes() || []
    }

    // 获取选中keys
    getCheckedKeys(): (string | number)[] {
        return this.treeRef?.getCheckedKeys() || []
    }

    // 设置选中keys（带防抖）
    setCheckedKeys(keys: (string | number)[]) {
        if (!this.treeRef || this.isProcessing) return

        this.isProcessing = true
        if (this.checkTimeout) clearTimeout(this.checkTimeout)

        this.checkTimeout = setTimeout(() => {
            // 单选逻辑：只能选一个
            if (keys.length > 1) {
                // 如果传入了多个key，只取最后一个（保持单选）
                keys = [keys[keys.length - 1]]
            }
            this.treeRef.setCheckedKeys(keys)
            this.isProcessing = false
        }, 50) as unknown as number
    }

    // 强制单选（取消其他选中）
    forceSingleCheck(key: string | number) {
        if (!this.treeRef || this.isProcessing) return

        this.isProcessing = true
        this.treeRef.setCheckedKeys([key])

        // 通知父组件
        const node = this.treeRef.getNode(key)
        if (node) {
            this.$emit('single-check-change', node.data, true, false)
        }

        this.isProcessing = false
    }

    // 过滤方法
    filterNodeMethod(value: string, data: TreeNode): boolean {
        if (!value) return true
        return data.name.toLowerCase().includes(value.toLowerCase())
    }

    // 事件处理
    onCheck(data: TreeNode, checkedNodes: { checkedNodes: TreeNode[] }) {
        this.$emit('check', data, checkedNodes)
    }

    onCheckChange(data: TreeNode, checked: boolean, indeterminate: boolean) {
        // 防抖处理
        if (this.checkTimeout) clearTimeout(this.checkTimeout)

        this.checkTimeout = setTimeout(() => {
            // 单选逻辑：如果选中了一个，确保只有一个被选中
            if (checked) {
                this.forceSingleCheck(data.id)
            }
            this.$emit('check-change', data, checked, indeterminate)
        }, 30) as unknown as number
    }

    // 清理资源
    cleanup() {
        if (this.checkTimeout) {
            clearTimeout(this.checkTimeout)
            this.checkTimeout = null
        }
        this.isProcessing = false
    }

    beforeDestroy() {
        this.cleanup()
    }

    @Watch('treeData')
    onTreeDataChange() {
        this.treeKey = Date.now()
    }
}